import React, { useEffect, useState } from 'react';
import { Breadcrumbs, FileUploader, Input, Multiselect } from '@cokitchen/cokitchen-components';
import PageHeaderText from 'components/base/page-header-text';
import useCreatePostContext from 'contexts/create-post.context';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import { Editor } from 'react-draft-wysiwyg';
import './styles.scss';
import { ContentState, convertFromHTML, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import useDashboardContext from 'contexts/dashboard.context';
import { generateKey } from 'utils/functions';
import SelectDropdown from 'components/base/select-dropdown';
import { ENUM_POST_TYPES } from 'utils/enums';

const CreatePostDetails = () => {
  const { createPostForm, setCreatePostForm, setDisabled, brands, meals, image, setImage } =
    useCreatePostContext();

  const html = createPostForm.body ? JSON.parse(createPostForm.body) : '';
  const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined);
  const [textEditorState, setTextEditorState] = useState<EditorState>(
    EditorState.createWithContent(
      ContentState.createFromBlockArray(
        convertFromHTML(html).contentBlocks,
        convertFromHTML(html).entityMap
      )
    )
  );
  const { polygons, cokitchens } = useDashboardContext();
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;

  const breadcrumbs = [
    { name: 'Manage homepage', path: ROUTE_KEYS.HOME },
    { name: 'New post', path: ROUTE_KEYS.CREATE_POST }
  ];

  const handleEditorStateChange = (e: EditorState) => {
    setTextEditorState(e);
    setCreatePostForm({
      ...createPostForm,
      body: JSON.stringify(draftToHtml(convertToRaw(e.getCurrentContent())))
    });
  };

  useEffect(() => {
    if (
      !createPostForm.heading ||
      !createPostForm.title ||
      !createPostForm.body ||
      !image ||
      !createPostForm.cokitchen_ids.length ||
      !createPostForm.post_type
    ) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [createPostForm]);

  const brandOptions = brands.map((b) => ({
    text: createPostForm.cokitchen_ids.length > 1 ? `${b.name} - ${b.cokitchen?.name}` : b.name,
    value: b.id
  }));

  const mealOptions = meals.map((m) => ({
    text: m.name,
    value: m.id
  }));

  const postOptions = [
    { id: ENUM_POST_TYPES.FCB, name: 'Fcb' },
    { id: ENUM_POST_TYPES.FC, name: 'Fc' },
    { id: ENUM_POST_TYPES.BUDGET, name: 'Budget' },
    { id: ENUM_POST_TYPES.SHOP, name: 'Shop' }
  ];

  return (
    <div className='mx-10 pt-8 max-w-[700px]'>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <div className='mt-8'>
        <PageHeaderText title='Create Post' description='Create a new meal lorem dolum ipsum' />
      </div>
      <Input
        type='text'
        label='Post heading'
        name='heading'
        value={createPostForm.heading}
        onChange={(e) =>
          setCreatePostForm({
            ...createPostForm,
            heading: e.target.value
          })
        }
        className='mb-6'
        placeholder='Post heading'
        inputClass='h-[50px]'
      />
      <Input
        type='text'
        label='Post title'
        name='title'
        value={createPostForm.title}
        onChange={(e) =>
          setCreatePostForm({
            ...createPostForm,
            title: e.target.value
          })
        }
        className='mb-6'
        placeholder='Eat Good, Pay Less.'
        inputClass='h-[50px]'
      />
      <p className='text-black mb-1.5'>Post body</p>
      <Editor
        editorState={textEditorState}
        toolbarClassName='toolbarClassName'
        wrapperClassName='wrapperClassName'
        editorClassName='editorClassName'
        onEditorStateChange={handleEditorStateChange}
        placeholder="You Know how you buy a really awesome gift for someone and you're just so excited out of your shoes for the person to experience it? 🤭 Think that, think Red Pepper! 🌶️📸
with ❤️"
      />
      <p className='font-medium mt-5'>Add post image</p>
      <FileUploader
        key={image}
        className='p-0 border-0 mb-8'
        token={token}
        setFileUrls={(urls) => {
          setImage(urls?.[0]);
        }}
        fileUrls={image ? [image] : []}
      />
      <div className='mb-8'>
        <label className='font-medium'>Cokitchens</label>
        <Multiselect
          placeholder='Select Cokitchen(s)'
          values={createPostForm.cokitchen_ids}
          options={cokitchens.map((c) => ({
            text: c.name,
            value: c.id
          }))}
          onChange={(cokitchen_ids) =>
            setCreatePostForm({
              ...createPostForm,
              cokitchen_ids
            })
          }
        />
      </div>
      <div className='mb-8'>
        <label className='font-medium'>Polygons</label>
        <Multiselect
          placeholder='Select Location(s)'
          values={createPostForm.location}
          options={polygons.map((p) => ({
            text: `${p.name} - ${p?.cokitchen?.name}`,
            value: p.id
          }))}
          onChange={(locations) =>
            setCreatePostForm({
              ...createPostForm,
              location: locations
            })
          }
        />
      </div>
      <div className='mb-8'>
        <label className='font-medium'>Brands</label>
        <Multiselect
          isSingle
          key={generateKey(brandOptions)}
          placeholder='Select brand'
          values={createPostForm.brand_id ? [createPostForm.brand_id] : []}
          options={brandOptions}
          onChange={(val) =>
            setCreatePostForm({
              ...createPostForm,
              brand_id: val?.[0],
              meal_id: ''
            })
          }
          hasSearch
          filterResults
        />
      </div>
      <div className='mb-8'>
        <label className='font-medium'>Meals</label>
        <Multiselect
          isSingle
          key={generateKey(mealOptions)}
          placeholder='Select meal'
          values={createPostForm.meal_id ? [createPostForm.meal_id] : []}
          options={mealOptions}
          onChange={(val) =>
            setCreatePostForm({
              ...createPostForm,
              meal_id: val?.[0]
            })
          }
          hasSearch
          filterResults
        />
      </div>
      <div className='mb-8'>
        <SelectDropdown
          label='Choose a Post Type'
          placeholder='Select post type'
          options={postOptions}
          value={selectedOption}
          setValue={(option) => {
            setSelectedOption(option.id);
            setCreatePostForm({
              ...createPostForm,
              post_type: option.id
            });
          }}
        />
      </div>
    </div>
  );
};

export default CreatePostDetails;
